import SimplexNoise from "../util/noise";
import palette from "../util/palette";

const simplex = new SimplexNoise(Math.random);

export const MAX_Z = 8000;
export const MIN_Z = 250;

const HEIGHT = 300;

const NOISE_SCALE = 0.0004;

const X_SPEED_FACTOR = -30;
const SPEED = 0.625;

function project(n, z)
{
    return n / (z * 0.002);
}

function noise(x, y)
{
    const v = simplex.noise2D(x,y);
    return v;
}

export const TERRAIN_PALETTE_SIZE = 32;
const TERRAIN_PALETTE = palette("#000000", "#ffffff", TERRAIN_PALETTE_SIZE);

const LOOKUP_ENTRY_SIZE = 3;

export function dump(lookup)
{
    let pos = 0;
    while (pos < lookup.length)
    {
        const color = lookup[pos++];
        const count = lookup[pos++];

        const end = pos + count;
        console.log("COLOR", color, lookup.slice(pos, end));

        pos = end;
    }


}

class Terrain
{
    constructor(ctx, x, y, width, height, startTime)
    {
        this.ctx = ctx;
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;

        this.start = startTime;

        this.xPos = 0;
        this.xSpeed = 0;

        this.fieldSize = 80 + this.width / 24;

        this.lookup = this.prepare();

    }

    prepare()
    {

        const { width, fieldSize} = this;

        // TERRAIN_PALETTE_SIZE entries with an empty array for each
        const byColor = new Array(TERRAIN_PALETTE_SIZE);

        const radius = MAX_Z - MIN_Z;

        const rSquared = radius * radius;

        const colorFactor = (TERRAIN_PALETTE_SIZE - 1) / radius;
        const halfWidth = width / 2;

        for (let circleZ = radius - fieldSize; circleZ > 0;  circleZ -= fieldSize)
        {
            const circleX = Math.sqrt(rSquared - circleZ * circleZ);

            const numDots = (circleX / fieldSize) | 0;

            const circleStart = -numDots * fieldSize;

            let xStart;

            const xProjected = project(circleStart, MIN_Z + circleZ);
            const projectedFieldSize = project(fieldSize, MIN_Z + circleZ);

            // does the circle start outside of the screen?
            if (xProjected < -(halfWidth + projectedFieldSize))
            {
                // -> start at the screen border
                const count = (halfWidth / projectedFieldSize)|0;

                // we start some field-sizes outside of the screen to be able to "push"
                // that point into the view via xOff in runtime
                xStart = -(count + 4 ) * fieldSize ;
            }
            else
            {
                // nope -> start of the circle is fine
                xStart = circleStart;
            }

            // end is always symmetric to start
            const xEnd = -xStart;

            const z = MIN_Z + circleZ;

            for (let x = xStart; x < xEnd; x += fieldSize)
            {
                const distance = Math.sqrt(x * x + z * z);
                const colorIndex = (TERRAIN_PALETTE_SIZE - distance * colorFactor) | 0;

                let array = byColor[colorIndex];

                if (!array)
                {
                    byColor[colorIndex] = array = [];
                }

                array.push(
                    x,
                    MIN_Z + z,
                    project(1, MIN_Z + z)
                );
            }
        }

        const lookup = [];

        for (let i = 1; i < TERRAIN_PALETTE_SIZE; i++)
        {
            const array = byColor[i];
            if (array)
            {
                lookup.push(i);
                lookup.push(array.length);
                lookup.push.apply(lookup, array);
            }
        }
        return Float32Array.from(lookup);

    }
    
    updateMouse(x,y)
    {
        this.xSpeed = (x / this.width - 0.5) * X_SPEED_FACTOR;
    }

    render(now, stats)
    {
        const { ctx, x, y, width, height, start, fieldSize, lookup} = this;

        ctx.fillStyle = "#000";
        ctx.fillRect(x,stats.terrainY - 1,width,height);

        const halfWidth = width / 2;
        const halfHeight = height / 2;

        const xPos = this.xPos;
        const zPos = -(now - start) * SPEED;

        const xOff = xPos % fieldSize;
        const xRest = xPos - xOff;
        const zOff = zPos % fieldSize;
        const screenY = this.y;

        let minY = Infinity;
        let pos = 0;
        let pointCount = 0;
        let overdraw = 0;
        let overLine = 0;
        while (pos < lookup.length)
        {
            const color = lookup[pos++];
            const count = lookup[pos++];

            const end = pos + count;

            ctx.fillStyle = TERRAIN_PALETTE[color];

            let overDrawInLine = true;
            for (let i = pos ; i < end; i += LOOKUP_ENTRY_SIZE)
            {
                let x = lookup[i];
                let z = lookup[i + 1];

                let noiseX = (x + xOff - xPos);
                let noiseZ = (z + zOff - zPos);


                const currentNoise = noise(noiseX * NOISE_SCALE, noiseZ * NOISE_SCALE);

                const zScale = (Math.cos(noiseZ * NOISE_SCALE) * 0.25 + 0.75);

                const y = halfHeight * 3 + (currentNoise * zScale * HEIGHT - HEIGHT/2);

                let xProjected = halfWidth  + project(x + xOff, z + zOff);
                let yProjected = screenY + height * 0.2 + project( y, z + zOff);
                ctx.fillRect( xProjected, yProjected, 2, 2);

                if (xProjected < 0 || xProjected > width || yProjected > height || yProjected < 0)
                {
                    overdraw++;
                }
                else
                {
                    overDrawInLine = false;
                }

                minY = Math.min(minY, yProjected);
                pointCount++;
            }

            if (overDrawInLine)
            {
                overLine++;
            }

            pos = end;
        }

        this.xPos += this.xSpeed;

        overdraw *= 100 / pointCount;

        stats.terrainY = minY;
        stats.count = Math.max(stats.count, pointCount);
        stats.overdraw = Math.max(stats.overdraw, overdraw);
        stats.overLine= Math.max(stats.overLine, overLine);
    }
}

export default Terrain;



// WEBPACK FOOTER //
// ./src/terrain/terrain.js